import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  api,
  type IptvChannel,
  type IptvGuideChannel,
  type IptvProbeResult,
  type IptvRecommendedSource,
  type IptvSource,
} from '../api.js';
import { LiveTvMultiView } from '../components/LiveTvMultiView.tsx';
import { PinDialog } from '../components/LiveTvPinDialog.tsx';
import { LiveTvRecommendedSources } from '../components/LiveTvRecommendedSources.tsx';
import { LiveTvSources } from '../components/LiveTvSources.tsx';
import {
  AlertIcon,
  ArrowLeftIcon,
  CloseIcon,
  FilmIcon,
  LockIcon,
  PlusIcon,
  SearchIcon,
  SettingsIcon,
  SparklesIcon,
  StarFilledIcon,
  StarIcon,
  TvIcon,
  UnlockIcon,
} from '../Icon.tsx';
import { useTvMode } from '../tvMode.js';
import { useHlsPlayer } from '../useHlsPlayer.js';

/**
 * Canlı TV (IPTV) ekranı.
 *
 * ## Tasarım
 *
 * Modern TiviMate ve televizyon yayın standartlarına uygun koyu tema:
 * Sol sütun **kanal tarayıcısı ve süzgeçler**, sağ sütun **canlı video oynatıcı**
 * ve eylem kontrolleri (geçmişe git/catch-up, kayıt/PVR, çoklu ekran).
 *
 * Akış adresi istemciye asla taşınmaz; sunucu imzalı bir oynatma jetonu
 * üretir ve akış relay üzerinden güvenli bir şekilde aktarılır.
 */
export interface LiveTvScreenProps {
  onBack: () => void;
}

interface ChannelRowProps {
  channel: IptvChannel;
  index: number;
  isPlaying: boolean;
  isFav: boolean;
  isLocked: boolean;
  onPlay: (channel: IptvChannel) => void;
  onToggleFavorite: (channelId: string) => void;
  onToggleLock: (channel: IptvChannel) => void;
}

function ChannelRow({
  channel,
  index,
  isPlaying,
  isFav,
  isLocked,
  onPlay,
  onToggleFavorite,
  onToggleLock,
}: ChannelRowProps): React.ReactElement {
  return (
    <li className={`live-tv__row${isPlaying ? ' is-active' : ''}`}>
      <button
        type="button"
        className="live-tv__channel"
        data-item-id={channel.id}
        onClick={() => onPlay(channel)}
        title={`${channel.name} kanalını oynat`}
      >
        <span className="live-tv__channel-index">
          {channel.channelNumber !== null
            ? String(channel.channelNumber).padStart(2, '0')
            : String(index + 1).padStart(2, '0')}
        </span>

        <div className="live-tv__logo-wrap">
          {channel.logo !== null ? (
            <img
              className="live-tv__logo"
              src={channel.logo}
              alt=""
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <span className="live-tv__logo-placeholder">
              {channel.name.slice(0, 2).toUpperCase()}
            </span>
          )}
        </div>

        <div className="live-tv__channel-info">
          <span className="live-tv__channel-name">{channel.name}</span>
          {channel.group !== null && <span className="live-tv__channel-meta">{channel.group}</span>}
        </div>

        {isPlaying && (
          <div className="live-tv__eq" title="Oynatılıyor">
            <span className="live-tv__eq-bar" />
            <span className="live-tv__eq-bar" />
            <span className="live-tv__eq-bar" />
          </div>
        )}
      </button>

      <div className="live-tv__row-actions">
        <button
          type="button"
          className={`live-tv__action-icon-btn${isFav ? ' is-active' : ''}`}
          aria-label={
            isFav ? `${channel.name} favorilerden çıkar` : `${channel.name} favorilere ekle`
          }
          aria-pressed={isFav}
          onClick={() => onToggleFavorite(channel.id)}
          title={isFav ? 'Favorilerden çıkar' : 'Favorilere ekle'}
        >
          {isFav ? <StarFilledIcon size={16} /> : <StarIcon size={16} />}
        </button>

        <button
          type="button"
          className={`live-tv__action-icon-btn live-tv__action-icon-btn--lock${
            isLocked ? ' is-active' : ''
          }`}
          aria-label={isLocked ? `${channel.name} kilidini aç` : `${channel.name} kilitle`}
          aria-pressed={isLocked}
          onClick={() => onToggleLock(channel)}
          title={isLocked ? 'Kilidi aç' : 'Çocuk kilidi koy'}
        >
          {isLocked ? <LockIcon size={16} /> : <UnlockIcon size={16} />}
        </button>
      </div>
    </li>
  );
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Canlı TV ekranı tüm IPTV akışını, rehberini, çalma listesi yönetimini ve oynatıcı kontrollerini koordine eder.
export function LiveTvScreen({ onBack }: LiveTvScreenProps): React.ReactElement {
  const { enabled: tvMode } = useTvMode();

  const [sources, setSources] = useState<IptvSource[]>([]);
  const [channels, setChannels] = useState<IptvChannel[]>([]);
  const [guide, setGuide] = useState<IptvGuideChannel[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [guideLoading, setGuideLoading] = useState(false);
  const [sourceModalOpen, setSourceModalOpen] = useState(false);
  const [onboardingTab, setOnboardingTab] = useState<'recommended' | 'custom'>('recommended');

  /** Rehber penceresi: [başlangıç, bitiş] — UTC epoch ms. */
  const [guideWindow, setGuideWindow] = useState<[number, number]>(() => {
    const now = Date.now();
    return [now - 60 * 60 * 1000, now + 6 * 60 * 60 * 1000];
  });
  const [activeChannel, setActiveChannel] = useState<IptvChannel | null>(null);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sourceId, setSourceId] = useState<string | ''>('');
  const [newSourceName, setNewSourceName] = useState('');
  const [newSourceUrl, setNewSourceUrl] = useState('');
  const [newSourceKind, setNewSourceKind] = useState<'m3u' | 'xtream'>('m3u');
  const [newSourceUser, setNewSourceUser] = useState('');
  const [newSourcePassword, setNewSourcePassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Kendi liste adresinin sunucudan ölçümü. */
  const [probe, setProbe] = useState<IptvProbeResult | null>(null);
  const [probing, setProbing] = useState(false);

  // W6: çocuk kilidi, catch-up, kayıt ve çoklu ekran durumu.
  const [locks, setLocks] = useState<string[]>([]);
  const [hasPin, setHasPin] = useState(false);
  const [pinDialog, setPinDialog] = useState<{
    mode: 'belirle' | 'dogrula';
    channel: IptvChannel | null;
  } | null>(null);
  /** Geçmişe gitme kayması (ms). 0 = canlı. */
  const [catchupOffset, setCatchupOffset] = useState(0);
  const [recording, setRecording] = useState(false);
  const [multiView, setMultiView] = useState(false);
  const [playbackToken, setPlaybackToken] = useState<string | null>(null);

  const player = useHlsPlayer(playbackUrl);

  const loadSources = useCallback(async () => {
    try {
      setSources(await api.iptvSources());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Kaynaklar okunamadı.');
    }
  }, []);

  const loadChannels = useCallback(async () => {
    try {
      setChannels(
        await api.iptvChannels({ source: sourceId === '' ? undefined : sourceId, q: query }),
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Kanallar okunamadı.');
    }
  }, [query, sourceId]);

  useEffect(() => {
    void loadSources();
    void api
      .iptvFavorites()
      .then(setFavorites)
      .catch(() => setFavorites([]));
  }, [loadSources]);

  useEffect(() => {
    void loadChannels();
  }, [loadChannels]);

  /** Kanalı favoriye ekler/çıkarır. */
  const toggleFavorite = useCallback(
    async (channelId: string) => {
      const isFavorite = favorites.includes(channelId);
      try {
        const next = isFavorite
          ? await api.removeIptvFavorite(channelId)
          : await api.addIptvFavorite(channelId);
        setFavorites(next);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Favori güncellenemedi.');
      }
    },
    [favorites],
  );

  /** Kanal seçildi: sunucudan oynatma jetonu iste, akışı başlat. */
  const playChannel = useCallback(
    async (channel: IptvChannel) => {
      setActiveChannel(channel);
      setError(null);
      setCatchupOffset(0);
      // Kilitli kanal: önce PIN doğrulanmalı.
      if (locks.includes(channel.id)) {
        setPinDialog({ mode: 'dogrula', channel });
        return;
      }
      try {
        const grant = await api.iptvPlayback(channel.id);
        setPlaybackUrl(grant.url);
        setPlaybackToken(new URL(grant.url, window.location.origin).searchParams.get('token'));
      } catch (cause) {
        setPlaybackUrl(null);
        setError(cause instanceof Error ? cause.message : 'Kanal açılamadı.');
      }
    },
    [locks],
  );

  /** Geçmişe gider (catch-up): arşiv playlist'ini relay üzerinden açar. */
  const seekCatchup = useCallback(
    async (offsetMs: number) => {
      if (activeChannel === null || playbackToken === null) return;
      setError(null);
      setCatchupOffset(offsetMs);
      if (offsetMs === 0) {
        // Canlıya dön: normal oynatma jetonunu tazele.
        try {
          const grant = await api.iptvPlayback(activeChannel.id);
          setPlaybackUrl(grant.url);
          setPlaybackToken(new URL(grant.url, window.location.origin).searchParams.get('token'));
        } catch (cause) {
          setError(cause instanceof Error ? cause.message : 'Kanal açılamadı.');
        }
        return;
      }
      const end = Date.now();
      const start = end - offsetMs;
      setPlaybackUrl(api.iptvCatchupUrl(activeChannel.id, playbackToken, start, end));
    },
    [activeChannel, playbackToken],
  );

  /** Kanal kaydını başlatır / durdurur. */
  const toggleRecording = useCallback(async () => {
    if (activeChannel === null) return;
    setBusy(true);
    setError(null);
    try {
      if (recording) {
        const list = await api.iptvRecordings();
        const running = list.find((item) => item.channelId === activeChannel.id);
        if (running) await api.deleteIptvRecording(running.id);
        setRecording(false);
      } else {
        await api.startIptvRecording(activeChannel.id);
        setRecording(true);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Kayıt başlatılamadı.');
    } finally {
      setBusy(false);
    }
  }, [activeChannel, recording]);

  /** Kanalı kilitler / kilidini açar. */
  const toggleLock = useCallback(
    async (channel: IptvChannel) => {
      setError(null);
      const isLocked = locks.includes(channel.id);
      if (!isLocked && !hasPin) {
        // PIN yoksa önce PIN belirlenmeli.
        setPinDialog({ mode: 'belirle', channel });
        return;
      }
      try {
        const next = isLocked
          ? await api.unlockIptvChannel(channel.id)
          : await api.lockIptvChannel(channel.id);
        setLocks(next);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Kilit değiştirilemedi.');
      }
    },
    [hasPin, locks],
  );

  /** PIN penceresinden sonra kilit durumunu tazeler. */
  const refreshLocks = useCallback(async () => {
    try {
      const state = await api.iptvLocks();
      setLocks(state.locks);
      setHasPin(state.hasPin);
    } catch {
      // Kilit durumu bilinemezse sessiz geçilir.
    }
  }, []);

  useEffect(() => {
    void refreshLocks();
  }, [refreshLocks]);

  useEffect(() => {
    if (player.error === null) return;
    setError(player.error);
  }, [player.error]);

  const addSource = useCallback(async () => {
    if (newSourceUrl.trim().length === 0) {
      setError('Çalma listesi adresi gerekli.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.addIptvSource({
        name: newSourceName.trim() === '' ? 'Çalma listesi' : newSourceName.trim(),
        url: newSourceUrl.trim(),
        kind: newSourceKind,
        ...(newSourceKind === 'xtream'
          ? { username: newSourceUser.trim(), password: newSourcePassword }
          : {}),
      });
      setNewSourceName('');
      setNewSourceUrl('');
      setNewSourceUser('');
      setNewSourcePassword('');
      setSourceModalOpen(false);
      await loadSources();
      await loadChannels();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Çalma listesi eklenemedi.');
    } finally {
      setBusy(false);
    }
  }, [
    loadChannels,
    loadSources,
    newSourceKind,
    newSourceName,
    newSourcePassword,
    newSourceUrl,
    newSourceUser,
  ]);

  /** Kendi liste adresini sunucudan ölçer. */
  const probeOwnUrl = useCallback(async () => {
    const url = newSourceUrl.trim();
    if (url.length === 0) {
      setError('Test etmek için bir adres girin.');
      return;
    }
    setProbing(true);
    setError(null);
    try {
      setProbe(await api.probeIptvUrl(url));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Adres ölçülemedi.');
    } finally {
      setProbing(false);
    }
  }, [newSourceUrl]);

  /** Ölçülmüş önerilen listeyi kaynak olarak ekler. */
  const addRecommended = useCallback(
    async (source: IptvRecommendedSource) => {
      setBusy(true);
      setError(null);
      try {
        await api.addIptvSource({ name: source.name, url: source.url, kind: 'm3u' });
        setSourceModalOpen(false);
        await loadSources();
        await loadChannels();
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Çalma listesi eklenemedi.');
      } finally {
        setBusy(false);
      }
    },
    [loadChannels, loadSources],
  );

  const groups = useMemo(() => {
    const seen = new Set<string>();
    for (const channel of channels) {
      if (channel.group !== null) seen.add(channel.group);
    }
    return [...seen].sort((a, b) => a.localeCompare(b, 'tr'));
  }, [channels]);

  /** Ekranda görünecek kanallar (favori ve grup süzgeci uygulanmış). */
  const visibleChannels = useMemo(() => {
    let list = channels;
    if (favoritesOnly) {
      list = list.filter((channel) => favorites.includes(channel.id));
    }
    if (activeGroup !== null) {
      list = list.filter((channel) => channel.group === activeGroup);
    }
    return list;
  }, [activeGroup, channels, favorites, favoritesOnly]);

  /** Rehberi yükler. */
  const loadGuide = useCallback(async () => {
    if (sources.length === 0) return;
    const selected = sourceId !== '' ? sourceId : (sources[0]?.id ?? '');
    if (selected === '') return;
    setGuideLoading(true);
    setError(null);
    setGuideOpen(true);
    try {
      await api.iptvRefreshEpg(selected);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? `Rehber tazelenemedi: ${cause.message}`
          : 'Rehber tazelenemedi; eski veri gösteriliyor.',
      );
    }
    try {
      const now = Date.now();
      const from = now - 60 * 60 * 1000;
      const to = now + 6 * 60 * 60 * 1000;
      setGuideWindow([from, to]);
      setGuide(await api.iptvEpg({ source: selected, from, to }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Rehber okunamadı.');
    } finally {
      setGuideLoading(false);
    }
  }, [sourceId, sources]);

  /** Kaynağı yeniden çeker (kanal listesi). */
  const refreshSource = useCallback(
    async (id: string) => {
      setBusy(true);
      setError(null);
      try {
        await api.refreshIptvSource(id);
        await loadSources();
        await loadChannels();
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Kaynak yenilenemedi.');
      } finally {
        setBusy(false);
      }
    },
    [loadChannels, loadSources],
  );

  /** Kaynağı siler (kanalları ve favorileri de silinir). */
  const removeSource = useCallback(
    async (id: string, name: string) => {
      if (typeof window !== 'undefined' && !window.confirm(`"${name}" kaynağı silinsin mi?`)) {
        return;
      }
      setBusy(true);
      setError(null);
      try {
        await api.deleteIptvSource(id);
        setSourceId((current) => (current === id ? '' : current));
        if (activeChannel?.sourceId === id) {
          setActiveChannel(null);
          setPlaybackUrl(null);
        }
        await loadSources();
        await loadChannels();
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Kaynak silinemedi.');
      } finally {
        setBusy(false);
      }
    },
    [activeChannel?.sourceId, loadChannels, loadSources],
  );

  /** Rehber ızgarasının saat sütunları (yerel saat). */
  const guideHours = useMemo(() => {
    const [from, to] = guideWindow;
    const hours: number[] = [];
    const start = new Date(from);
    start.setMinutes(0, 0, 0);
    for (let time = start.getTime(); time <= to; time += 60 * 60 * 1000) {
      hours.push(time);
    }
    return hours;
  }, [guideWindow]);

  /** Programın ızgaradaki konumu (yüzde). */
  const programmeStyle = useCallback(
    (startsAt: number, stopsAt: number): React.CSSProperties => {
      const [from, to] = guideWindow;
      const span = Math.max(to - from, 1);
      const left = Math.max(0, ((startsAt - from) / span) * 100);
      const width = Math.max(1, ((Math.min(stopsAt, to) - Math.max(startsAt, from)) / span) * 100);
      return { left: `${left}%`, width: `${width}%` };
    },
    [guideWindow],
  );

  const hourLabel = (time: number): string =>
    new Date(time).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

  // Favori kanalları hızlı başlatmak için çek
  const favoriteChannels = useMemo(
    () => channels.filter((c) => favorites.includes(c.id)),
    [channels, favorites],
  );

  return (
    <section className={`live-tv${tvMode ? ' live-tv--tv' : ''}`}>
      {/* --- Üst Başlık & Eylem Çubuğu ------------------------------------- */}
      <header className="live-tv__header">
        <div className="live-tv__header-left">
          <button type="button" className="btn btn--ghost" onClick={onBack} title="Geri dön">
            <ArrowLeftIcon size={18} />
            <span>Geri</span>
          </button>
          <div className="live-tv__title-group">
            <h1 className="live-tv__title">Canlı TV</h1>
            {activeChannel !== null && (
              <span className="live-tv__live-badge" title="Canlı akış aktif">
                <span className="live-tv__live-dot" />
                <span>CANLI</span>
              </span>
            )}
          </div>
        </div>

        <div className="live-tv__header-actions">
          {(sources.length > 0 || channels.length > 0) && (
            <>
              <button
                type="button"
                className={`btn${guideOpen ? ' btn--primary' : ' btn--ghost'}`}
                onClick={() => {
                  if (guideOpen) {
                    setGuideOpen(false);
                  } else {
                    void loadGuide();
                  }
                }}
                disabled={guideLoading}
                title="Elektronik Program Rehberi (EPG)"
              >
                <TvIcon size={16} />
                <span>
                  {guideLoading ? 'Rehber yükleniyor…' : guideOpen ? 'Rehberi Kapat' : 'Rehber'}
                </span>
              </button>

              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setMultiView(true)}
                disabled={channels.length === 0}
                title="Aynı anda 4 kanalı izle"
              >
                <FilmIcon size={16} />
                <span>Çoklu Ekran</span>
              </button>

              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setSourceModalOpen(true)}
                title="Çalma listelerini yönet veya yeni liste ekle"
              >
                <SettingsIcon size={16} />
                <span>Kaynaklar ({sources.length})</span>
              </button>
            </>
          )}
        </div>
      </header>

      {/* --- Durum A: Henüz Kaynak ve Kanal Yokken (Şık Karşılama Kartı) ------- */}
      {sources.length === 0 && channels.length === 0 ? (
        <div className="live-tv__onboarding">
          <div className="live-tv__onboarding-hero">
            <span className="live-tv__onboarding-badge">
              <SparklesIcon size={14} />
              <span>IPTV & Canlı Yayın Katmanı</span>
            </span>
            <h2 className="live-tv__onboarding-title">Canlı TV Dünyasına Hoş Geldiniz</h2>
            <p className="live-tv__onboarding-lead">
              Henüz çalma listesi eklenmedi. Başlamak için yasal ve ücretsiz önerilen ulusal/FAST
              kanallarını sunucudan tek tıkla ekleyebilir veya kendi M3U / Xtream çalma listenizi
              bağlayabilirsiniz.
            </p>
          </div>

          <div className="live-tv__tabs">
            <button
              type="button"
              className={`chip${onboardingTab === 'recommended' ? ' is-active' : ''}`}
              onClick={() => setOnboardingTab('recommended')}
            >
              <SparklesIcon size={14} />
              <span>Önerilen Kanallar</span>
            </button>
            <button
              type="button"
              className={`chip${onboardingTab === 'custom' ? ' is-active' : ''}`}
              onClick={() => setOnboardingTab('custom')}
            >
              <PlusIcon size={14} />
              <span>Kendi Çalma Listenizi Ekleyin</span>
            </button>
          </div>

          {onboardingTab === 'recommended' ? (
            <div className="live-tv__card">
              <div className="live-tv__card-header">
                <div className="live-tv__card-icon">
                  <TvIcon size={22} />
                </div>
                <div>
                  <h3 className="live-tv__card-title">Yasal ve Ücretsiz Yayınlar</h3>
                  <p className="live-tv__card-desc">
                    TRT, ulusal açık ve FAST reklam destekli yayınlar sunucunuzdan test edilir ve
                    anında kütüphanenize eklenir.
                  </p>
                </div>
              </div>
              <LiveTvRecommendedSources
                onAdd={(source) => void addRecommended(source)}
                busy={busy}
              />
            </div>
          ) : (
            <div className="live-tv__card">
              <div className="live-tv__card-header">
                <div className="live-tv__card-icon">
                  <PlusIcon size={22} />
                </div>
                <div>
                  <h3 className="live-tv__card-title">Özel Çalma Listesi Tanımla</h3>
                  <p className="live-tv__card-desc">
                    M3U formatında URL veya Xtream Codes API sunucu bilgilerinizi girin.
                  </p>
                </div>
              </div>

              <form
                className="live-tv__add-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  void addSource();
                }}
              >
                <div className="live-tv__kinds">
                  <button
                    type="button"
                    className={`chip${newSourceKind === 'm3u' ? ' is-active' : ''}`}
                    onClick={() => setNewSourceKind('m3u')}
                  >
                    M3U listesi
                  </button>
                  <button
                    type="button"
                    className={`chip${newSourceKind === 'xtream' ? ' is-active' : ''}`}
                    onClick={() => setNewSourceKind('xtream')}
                  >
                    Xtream portalı
                  </button>
                </div>

                <div className="live-tv__form-row">
                  <label htmlFor="source-name" className="live-tv__form-label">
                    Liste Adı
                  </label>
                  <input
                    id="source-name"
                    className="input"
                    type="text"
                    placeholder="Ad (örn. Ev listesi)"
                    value={newSourceName}
                    onChange={(event) => setNewSourceName(event.target.value)}
                  />
                </div>

                <div className="live-tv__form-row">
                  <label htmlFor="source-url" className="live-tv__form-label">
                    {newSourceKind === 'xtream' ? 'Portal Sunucu Adresi' : 'M3U Adresi'}
                  </label>
                  <input
                    id="source-url"
                    className="input"
                    type="text"
                    placeholder={
                      newSourceKind === 'xtream'
                        ? 'http://portal.ornek.com:8080'
                        : 'https://ornek.com/playlist.m3u'
                    }
                    value={newSourceUrl}
                    onChange={(event) => setNewSourceUrl(event.target.value)}
                  />
                </div>

                {newSourceKind === 'xtream' && (
                  <>
                    <div className="live-tv__form-row">
                      <label htmlFor="xtream-user" className="live-tv__form-label">
                        Kullanıcı Adı
                      </label>
                      <input
                        id="xtream-user"
                        className="input"
                        type="text"
                        placeholder="Portal kullanıcı adı"
                        autoComplete="off"
                        value={newSourceUser}
                        onChange={(event) => setNewSourceUser(event.target.value)}
                      />
                    </div>
                    <div className="live-tv__form-row">
                      <label htmlFor="xtream-pass" className="live-tv__form-label">
                        Parola
                      </label>
                      <input
                        id="xtream-pass"
                        className="input"
                        type="password"
                        placeholder="Portal parolası"
                        autoComplete="off"
                        value={newSourcePassword}
                        onChange={(event) => setNewSourcePassword(event.target.value)}
                      />
                    </div>
                    <p className="live-tv__hint">
                      Parola sunucuda Argon2id/AES ile şifrelenerek saklanır ve asla istemciye geri
                      gönderilmez.
                    </p>
                  </>
                )}

                <div className="live-tv__form-actions">
                  <button type="submit" className="btn btn--primary" disabled={busy}>
                    <PlusIcon size={16} />
                    <span>{busy ? 'Ekleniyor…' : 'Ekle'}</span>
                  </button>
                  {newSourceKind === 'm3u' && (
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => void probeOwnUrl()}
                      disabled={probing || newSourceUrl.trim().length === 0}
                    >
                      <SparklesIcon size={16} />
                      <span>{probing ? 'Ölçülüyor…' : 'Bu adresi test et'}</span>
                    </button>
                  )}
                </div>

                {probe !== null && (
                  <div className="live-tv__probe-badge">
                    {probe.error !== null
                      ? `Liste okunamadı: ${probe.error}`
                      : `${probe.working}/${probe.tested} kanal açılıyor (toplam ${probe.total}). Ölçüm sunucudan yapıldı.`}
                  </div>
                )}
              </form>
            </div>
          )}

          {error !== null && <p className="live-tv__error">{error}</p>}
        </div>
      ) : (
        /* --- Durum B: Kaynaklar Mevcutken (Modern 2-Sütun Düzeni) ------------ */
        <div className="live-tv__body">
          {/* Sol Sütun: Kanal Tarayıcısı */}
          <aside className="live-tv__side">
            <div className="live-tv__filters">
              <div className="live-tv__search-box">
                <SearchIcon size={18} className="live-tv__search-icon" />
                <input
                  className="input live-tv__search-input"
                  type="search"
                  placeholder="Kanal ara…"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  aria-label="Kanal ara"
                />
                {query !== '' && (
                  <button
                    type="button"
                    className="live-tv__search-clear"
                    onClick={() => setQuery('')}
                    title="Aramayı temizle"
                  >
                    <CloseIcon size={14} />
                  </button>
                )}
              </div>

              {sources.length > 1 && (
                <div className="live-tv__source-select-wrapper">
                  <select
                    className="input live-tv__source-select"
                    value={sourceId}
                    onChange={(event) => setSourceId(event.target.value)}
                    aria-label="Kaynak seçin"
                  >
                    <option value="">Tüm kaynaklar ({channels.length} kanal)</option>
                    {sources.map((source) => (
                      <option key={source.id} value={source.id}>
                        {source.name} ({source.channelCount} kanal)
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Kategori ve Favori Hapları */}
            <div className="live-tv__groups">
              <button
                type="button"
                className={`chip${!favoritesOnly && activeGroup === null ? ' is-active' : ''}`}
                onClick={() => {
                  setFavoritesOnly(false);
                  setActiveGroup(null);
                }}
              >
                Tümü ({channels.length})
              </button>
              <button
                type="button"
                className={`chip${favoritesOnly ? ' is-active' : ''}`}
                onClick={() => {
                  setFavoritesOnly((val) => !val);
                  setActiveGroup(null);
                }}
              >
                <StarFilledIcon size={12} style={{ color: 'var(--warn)' }} />
                <span>Favoriler ({favorites.length})</span>
              </button>
              {groups.map((group) => (
                <button
                  key={group}
                  type="button"
                  className={`chip${activeGroup === group ? ' is-active' : ''}`}
                  onClick={() => {
                    setActiveGroup((current) => (current === group ? null : group));
                    setFavoritesOnly(false);
                  }}
                >
                  {group}
                </button>
              ))}
            </div>

            <div className="live-tv__list-stats">
              <span>{visibleChannels.length} kanal listeleniyor</span>
              {favoritesOnly && <span>Sadece favoriler</span>}
            </div>

            {/* Kanal Listesi */}
            <ul className="live-tv__list">
              {visibleChannels.map((channel, index) => (
                <ChannelRow
                  key={channel.id}
                  channel={channel}
                  index={index}
                  isPlaying={activeChannel?.id === channel.id}
                  isFav={favorites.includes(channel.id)}
                  isLocked={locks.includes(channel.id)}
                  onPlay={(c) => void playChannel(c)}
                  onToggleFavorite={(id) => void toggleFavorite(id)}
                  onToggleLock={(c) => void toggleLock(c)}
                />
              ))}

              {visibleChannels.length === 0 && (
                <li className="live-tv__empty-channels">
                  <TvIcon size={32} />
                  <span>
                    {favoritesOnly
                      ? 'Henüz favori kanal eklenmedi.'
                      : activeGroup !== null
                        ? `"${activeGroup}" grubunda kanal bulunamadı.`
                        : query !== ''
                          ? 'Aramanızla eşleşen kanal bulunamadı.'
                          : 'Eşleşen kanal yok.'}
                  </span>
                  {(query !== '' || favoritesOnly || activeGroup !== null) && (
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => {
                        setQuery('');
                        setFavoritesOnly(false);
                        setActiveGroup(null);
                      }}
                    >
                      Filtreleri Temizle
                    </button>
                  )}
                </li>
              )}
            </ul>
          </aside>

          {/* Sağ Sütun: Canlı Oynatıcı & Kontroller */}
          <main className="live-tv__player-panel">
            <div className="live-tv__stage">
              <video
                ref={player.videoRef}
                className="live-tv__video"
                controls
                playsInline
                autoPlay
                muted
                aria-label={activeChannel?.name ?? 'Canlı TV Oynatıcı'}
              />

              {activeChannel === null && (
                <div className="live-tv__placeholder">
                  <div className="live-tv__placeholder-icon">
                    <TvIcon size={36} />
                  </div>
                  <h3 className="live-tv__placeholder-title">İzlemek İçin Bir Kanal Seçin</h3>
                  <p className="live-tv__placeholder-text">
                    Soldaki listeden dilediğiniz televizyon kanalına tıklayarak kesintisiz canlı
                    yayını başlatabilirsiniz.
                  </p>
                  {favoriteChannels.length > 0 && (
                    <div className="live-tv__placeholder-favorites">
                      {favoriteChannels.slice(0, 4).map((fav) => (
                        <button
                          key={fav.id}
                          type="button"
                          className="chip"
                          onClick={() => void playChannel(fav)}
                        >
                          <StarFilledIcon size={12} style={{ color: 'var(--warn)' }} />
                          <span>{fav.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Oynatıcı Başlık & Bilgi */}
            {activeChannel !== null && (
              <div className="live-tv__player-info">
                <div className="live-tv__now-playing">
                  <div className="live-tv__now-logo">
                    {activeChannel.logo !== null ? (
                      <img
                        src={activeChannel.logo}
                        alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    ) : (
                      <TvIcon size={20} />
                    )}
                  </div>
                  <div className="live-tv__now-details">
                    <h2 className="live-tv__now-title">{activeChannel.name}</h2>
                    <div className="live-tv__now-meta">
                      {activeChannel.group !== null && (
                        <span className="live-tv__badge-group">{activeChannel.group}</span>
                      )}
                      <span>HLS Canlı Yayın</span>
                      {catchupOffset > 0 && (
                        <span style={{ color: 'var(--accent)' }}>
                          · {catchupOffset / (60 * 1000)} dk geçmişten
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="live-tv__live-badge">
                  <span className="live-tv__live-dot" />
                  <span>{catchupOffset === 0 ? 'CANLI YAYIN' : 'GERİ SARMA'}</span>
                </div>
              </div>
            )}

            {error !== null && (
              <div className="live-tv__error">
                <AlertIcon size={16} />
                <span>{error}</span>
              </div>
            )}

            {/* Oynatıcı Eylemleri (Catchup & PVR) */}
            <div className="live-tv__player-actions">
              <div className="live-tv__playback-controls">
                <button
                  type="button"
                  className={`chip${catchupOffset === 30 * 60 * 1000 ? ' is-active' : ''}`}
                  onClick={() => void seekCatchup(30 * 60 * 1000)}
                  disabled={activeChannel === null}
                  title="30 dakika geriye sar"
                >
                  30 dk önce
                </button>
                <button
                  type="button"
                  className={`chip${catchupOffset === 2 * 60 * 60 * 1000 ? ' is-active' : ''}`}
                  onClick={() => void seekCatchup(2 * 60 * 60 * 1000)}
                  disabled={activeChannel === null}
                  title="2 saat geriye sar"
                >
                  2 saat önce
                </button>
                <button
                  type="button"
                  className={`chip${catchupOffset === 0 ? ' is-active' : ''}`}
                  onClick={() => void seekCatchup(0)}
                  disabled={activeChannel === null}
                  title="Doğrudan canlı yayına dön"
                >
                  <span className="live-tv__live-dot" />
                  <span>Canlı</span>
                </button>
              </div>

              <div className="live-tv__playback-controls">
                <button
                  type="button"
                  className={`chip live-tv__record-chip${recording ? ' is-active' : ''}`}
                  onClick={() => void toggleRecording()}
                  disabled={activeChannel === null || busy}
                  title="Yayını sunucuya kaydet (PVR)"
                >
                  <span className="live-tv__record-dot" />
                  <span>{recording ? 'Kaydı durdur' : 'Kaydet'}</span>
                </button>
              </div>
            </div>
          </main>
        </div>
      )}

      {/* --- Elektronik Program Rehberi (EPG) Izgarası -------------------- */}
      {guideOpen && (
        <section className="live-tv__guide" aria-label="Yayın rehberi">
          <div className="live-tv__guide-header-bar">
            <h3 className="live-tv__subtitle">Yayın Akışı & Program Rehberi (EPG)</h3>
            <button type="button" className="btn btn--ghost" onClick={() => setGuideOpen(false)}>
              Rehberi Kapat
            </button>
          </div>

          <div className="live-tv__guide-head">
            <span className="live-tv__guide-corner">Kanal</span>
            {guideHours.map((time) => (
              <span key={time} className="live-tv__guide-hour">
                {hourLabel(time)}
              </span>
            ))}
          </div>

          {guide.length === 0 && (
            <p className="live-tv__empty-channels" style={{ padding: 24 }}>
              Bu zaman penceresi için rehber verisi bulunamadı. Kaynağın rehber (EPG/XMLTV) adresi
              tanımlı olmalıdır.
            </p>
          )}

          {guide.map((gChannel) => (
            <div key={gChannel.key} className="live-tv__guide-row">
              <div className="live-tv__guide-name" title={gChannel.name}>
                <span>{gChannel.name}</span>
              </div>
              <div className="live-tv__guide-track">
                {gChannel.programmes.map((programme) => (
                  <div
                    key={`${programme.startsAt}-${programme.title}`}
                    className="live-tv__guide-program"
                    style={programmeStyle(programme.startsAt, programme.stopsAt)}
                    title={`${programme.title}\n${programme.description ?? ''}`}
                  >
                    {programme.title}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}

      {/* --- Kaynak Yönetimi Modalı --------------------------------------- */}
      {sourceModalOpen && (
        <div
          className="live-tv__modal"
          role="dialog"
          aria-modal="true"
          aria-label="Kaynak Yönetimi"
        >
          <div className="live-tv__modal-box">
            <div className="live-tv__modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <SettingsIcon size={20} />
                <h2 className="live-tv__modal-title">Çalma Listeleri & Kaynaklar</h2>
              </div>
              <button
                type="button"
                className="live-tv__action-icon-btn"
                onClick={() => setSourceModalOpen(false)}
                title="Kapat"
              >
                <CloseIcon size={18} />
              </button>
            </div>

            <LiveTvSources
              sources={sources}
              busy={busy}
              onRefresh={(id) => void refreshSource(id)}
              onRemove={(id, name) => void removeSource(id, name)}
            />

            <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 16 }}>
              <h3 className="live-tv__subtitle" style={{ marginBottom: 12 }}>
                Yeni Kaynak Ekle
              </h3>
              <form
                className="live-tv__add-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  void addSource();
                }}
              >
                <div className="live-tv__kinds">
                  <button
                    type="button"
                    className={`chip${newSourceKind === 'm3u' ? ' is-active' : ''}`}
                    onClick={() => setNewSourceKind('m3u')}
                  >
                    M3U listesi
                  </button>
                  <button
                    type="button"
                    className={`chip${newSourceKind === 'xtream' ? ' is-active' : ''}`}
                    onClick={() => setNewSourceKind('xtream')}
                  >
                    Xtream portalı
                  </button>
                </div>

                <input
                  className="input"
                  type="text"
                  placeholder="Ad (örn. Ev listesi)"
                  value={newSourceName}
                  onChange={(event) => setNewSourceName(event.target.value)}
                />
                <input
                  className="input"
                  type="text"
                  placeholder={newSourceKind === 'xtream' ? 'Portal adresi' : 'M3U adresi'}
                  value={newSourceUrl}
                  onChange={(event) => setNewSourceUrl(event.target.value)}
                />

                {newSourceKind === 'xtream' && (
                  <>
                    <input
                      className="input"
                      type="text"
                      placeholder="Portal kullanıcı adı"
                      autoComplete="off"
                      value={newSourceUser}
                      onChange={(event) => setNewSourceUser(event.target.value)}
                    />
                    <input
                      className="input"
                      type="password"
                      placeholder="Portal parolası"
                      autoComplete="off"
                      value={newSourcePassword}
                      onChange={(event) => setNewSourcePassword(event.target.value)}
                    />
                    <p className="live-tv__hint">
                      Parola sunucuda Argon2id/AES ile şifrelenerek saklanır ve asla istemciye geri
                      gönderilmez.
                    </p>
                  </>
                )}

                <div className="live-tv__form-actions">
                  <button type="submit" className="btn btn--primary" disabled={busy}>
                    <PlusIcon size={16} />
                    <span>{busy ? 'Ekleniyor…' : 'Ekle'}</span>
                  </button>
                  {newSourceKind === 'm3u' && (
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => void probeOwnUrl()}
                      disabled={probing || newSourceUrl.trim().length === 0}
                    >
                      <SparklesIcon size={16} />
                      <span>{probing ? 'Ölçülüyor…' : 'Bu adresi test et'}</span>
                    </button>
                  )}
                </div>

                {probe !== null && (
                  <div className="live-tv__probe-badge">
                    {probe.error !== null
                      ? `Liste okunamadı: ${probe.error}`
                      : `${probe.working}/${probe.tested} kanal açılıyor (toplam ${probe.total}). Ölçüm sunucudan yapıldı.`}
                  </div>
                )}
              </form>
            </div>

            <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 16 }}>
              <LiveTvRecommendedSources
                onAdd={(source) => void addRecommended(source)}
                busy={busy}
              />
            </div>

            <div className="live-tv__modal-actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setSourceModalOpen(false)}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- Çoklu Ekran Modalı ------------------------------------------- */}
      {multiView && <LiveTvMultiView channels={channels} onClose={() => setMultiView(false)} />}

      {/* --- PIN Diyaloğu ------------------------------------------------- */}
      {pinDialog !== null && (
        <PinDialog
          key={`${pinDialog.mode}-${pinDialog.channel?.id ?? ''}`}
          mode={pinDialog.mode}
          onCancel={() => setPinDialog(null)}
          onSubmit={async (pin: string) => {
            const target = pinDialog.channel;
            if (pinDialog.mode === 'belirle') {
              await api.setIptvPin(pin);
              await refreshLocks();
              if (target !== null) {
                await api.lockIptvChannel(target.id);
                await refreshLocks();
              }
              setPinDialog(null);
              return;
            }
            if (target === null) {
              setPinDialog(null);
              return;
            }
            await api.verifyIptvPin(target.id, pin);
            setPinDialog(null);
            await playChannel(target);
          }}
        />
      )}
    </section>
  );
}
